/**
 * Accessible ownership for Moonfall's whole-app portrait blocker.
 *
 * responsive.css remains the visual authority. This module mirrors the same
 * media query so portrait phone/tablet users cannot tab or be announced into
 * the application beneath the rotate dialog. Every sibling's prior state is
 * restored exactly when landscape returns.
 */
import { StoryFeed } from './story-feed.js';
let OrientationGuard = {};

(function () {
  'use strict';


  const QUERY = '(orientation: portrait) and (max-width: 1024px)';
  const overlay = document.getElementById('rotate-overlay');
  const media = typeof window.matchMedia === 'function' ? window.matchMedia(QUERY) : null;
  const siblingState = new Map();
  let active = false;
  let previousFocus = null;
  let observer = null;
  let overlayState = null;

  function focusWithoutScroll(element) {
    if (!element || typeof element.focus !== 'function') return;
    try {
      element.focus({ preventScroll: true });
    } catch (_) {
      element.focus();
    }
  }

  function isolateSibling(element) {
    if (!active || !element || element.nodeType !== 1 || element === overlay) return;
    if (element.tagName === 'SCRIPT' || siblingState.has(element)) return;
    siblingState.set(element, {
      inert: element.inert === true,
      ariaHidden: element.getAttribute('aria-hidden'),
    });
    element.inert = true;
    element.setAttribute('aria-hidden', 'true');
  }

  function restoreSiblings() {
    siblingState.forEach(({ inert, ariaHidden }, element) => {
      if (!element.isConnected) return;
      element.inert = inert;
      if (ariaHidden === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', ariaHidden);
    });
    siblingState.clear();
  }

  /**
   * The dialog's controls, in order, when they are on screen: the way out of
   * an app's own browser, and Copy link. The head bundle marks <html> for
   * what is shown (in-app-browser-flag.js) and responsive.css draws it from
   * the same marks, so the marks are read here rather than asked of layout:
   *   - a link known to leave the app: the link, and Copy link beside it once
   *     a tap on the link went nowhere (`data-in-app-fallback`);
   *   - none (TikTok): Copy link alone.
   * While the card is held for its language nothing on it can take focus.
   */
  function wayOutControls() {
    const root = document.documentElement;
    if (!overlay || root.hasAttribute('data-in-app-copy')) return [];
    const variant = root.getAttribute('data-in-app-way-out');
    const link = overlay.querySelector('#rotate-overlay-way-out-link');
    const copy = overlay.querySelector('#rotate-overlay-way-out-copy');
    if (variant === 'button') {
      return [link, root.getAttribute('data-in-app-fallback') === 'shown' ? copy : null].filter(Boolean);
    }
    if (variant === 'menu') return [copy].filter(Boolean);
    return [];
  }

  /* Tab never leaves the dialog. With controls shown it moves among them,
     forwards or backwards, and Enter or Space works each as it always does;
     with none it stays on the dialog itself. */
  function trapFocus(event) {
    if (!active || event.key !== 'Tab') return;
    event.preventDefault();
    const controls = wayOutControls();
    if (!controls.length) {
      focusWithoutScroll(overlay);
      return;
    }
    const at = controls.indexOf(document.activeElement);
    const step = event.shiftKey ? -1 : 1;
    const next = at < 0
      ? (event.shiftKey ? controls.length - 1 : 0)
      : (at + step + controls.length) % controls.length;
    focusWithoutScroll(controls[next]);
  }

  function activate() {
    if (active || !overlay || !document.body) return;

    // Portrait is the whole-app modal authority. Release any open manga dialog
    // before taking our snapshot so the two modal owners never restore each
    // other's inert/aria-hidden state out of order.
    try {
      StoryFeed?.dismissMangaZoom?.();
    } catch (_) { /* the rotate guard must still activate */ }

    overlayState = {
      inert: overlay.inert === true,
      ariaHidden: overlay.getAttribute('aria-hidden'),
    };
    active = true;
    const focused = document.activeElement;
    previousFocus = focused && focused !== document.body && focused !== overlay ? focused : null;
    overlay.inert = false;
    overlay.setAttribute('aria-hidden', 'false');
    focusWithoutScroll(overlay);
    Array.from(document.body.children).forEach(isolateSibling);
    document.addEventListener('keydown', trapFocus, true);

    if (typeof MutationObserver === 'function') {
      observer = new MutationObserver((records) => {
        records.forEach((record) => {
          Array.from(record.addedNodes).forEach(isolateSibling);
        });
      });
      observer.observe(document.body, { childList: true });
    }
  }

  function deactivate() {
    if (!active || !overlay) return;
    active = false;
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    document.removeEventListener('keydown', trapFocus, true);
    restoreSiblings();
    if (overlayState) {
      overlay.inert = overlayState.inert;
      if (overlayState.ariaHidden === null) overlay.removeAttribute('aria-hidden');
      else overlay.setAttribute('aria-hidden', overlayState.ariaHidden);
      overlayState = null;
    } else {
      overlay.inert = false;
      overlay.setAttribute('aria-hidden', 'true');
    }

    const focusTarget = previousFocus;
    previousFocus = null;
    if (focusTarget?.isConnected) focusWithoutScroll(focusTarget);
    else if (document.activeElement === overlay && typeof overlay.blur === 'function') overlay.blur();
  }

  function sync(event) {
    const shouldBlock = typeof event?.matches === 'boolean' ? event.matches : media?.matches === true;
    if (shouldBlock) activate();
    else deactivate();
  }

  function destroy() {
    deactivate();
    if (media?.removeEventListener) media.removeEventListener('change', sync);
    else if (media?.removeListener) media.removeListener(sync);
  }

  OrientationGuard = {
    destroy,
    isActive: () => active,
    sync,
  };

  if (media?.addEventListener) media.addEventListener('change', sync);
  else if (media?.addListener) media.addListener(sync);
  sync();
})();

export { OrientationGuard };
